import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuth } from '../hooks';
import { AuthShell, authInputStyle, authInputFocus, authInputBlur, authLabelStyle, authButtonStyle } from '../components/AuthShell';

const RegisterPage: React.FC = () => {
  const { register } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', password: '', confirmPassword: '', agreeToTerms: false, honeypot: ''
  });
  const [formStartTime, setFormStartTime] = useState(0);
  const [lastSubmitTime, setLastSubmitTime] = useState(0);
  const [interactionCount, setInteractionCount] = useState(0);

  useEffect(() => { setFormStartTime(Date.now()); }, []);

  const trackInteraction = () => setInteractionCount(c => c + 1);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError(null);

    const now = Date.now();
    if (formData.honeypot) { setError('Please try again later'); return; }
    if (now - formStartTime < 5000) { setError('Please take your time to fill out the form'); return; }
    if (lastSubmitTime > 0 && now - lastSubmitTime < 30000) { setError(`Please wait before trying again`); return; }
    if (interactionCount < 3) { setError('Please complete all required fields'); return; }
    if (formData.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }
    if (!formData.agreeToTerms) { setError('You must agree to the terms'); return; }

    setIsSubmitting(true);
    setLastSubmitTime(now);
    try {
      const result = await register({ name: formData.name, email: formData.email, phone: formData.phone, password: formData.password });
      if (result.success) { setIsSuccess(true); } else { setError(result.error || 'Registration failed'); }
    } catch (err: any) {
      setError(err.message || 'Network error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    trackInteraction();
    setFormData(p => ({ ...p, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  };

  if (isSuccess) {
    return (
      <>
        <SEO title="Registration Successful - RRD Planners" description="Account created" />
        <AuthShell>
          <div style={{ textAlign: 'center' }}>
            <CheckCircle size={48} style={{ color: '#28a745', margin: '0 auto 16px' }} />
            <h2 style={{ color: '#fff', fontSize: 24, fontWeight: 700, marginBottom: 8 }}>Account Created!</h2>
            <p style={{ color: '#888', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
              Please check your email to verify your account, then sign in.
            </p>
            <a href="/auth/login" style={{
              display: 'inline-block', padding: '12px 32px', background: '#FFD93D', color: '#1a1a1a',
              borderRadius: 10, fontWeight: 700, textDecoration: 'none', fontSize: 15
            }}>
              Go to Sign In
            </a>
          </div>
        </AuthShell>
      </>
    );
  }

  return (
    <>
      <SEO title="Sign Up - RRD Planners" description="Create your RRD Planners account" />
      <AuthShell>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <h2 style={{ color: '#fff', fontSize: 28, fontWeight: 700 }}>Create Account</h2>
          <p style={{ color: '#666', fontSize: 14, marginTop: 8 }}>Sign up to start generating plot maps</p>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div style={{ background: 'rgba(229,115,115,0.1)', border: '1px solid #e57373', borderRadius: 10, padding: '10px 14px', marginBottom: 16, color: '#e57373', fontSize: 13 }}>
              {error}
            </div>
          )}

          {/* Honeypot */}
          <div style={{ position: 'absolute', left: -9999, opacity: 0, height: 0 }}>
            <input type="text" name="website" value={formData.honeypot} onChange={e => setFormData(p => ({ ...p, honeypot: e.target.value }))} tabIndex={-1} autoComplete="off" />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={authLabelStyle}>Full Name</label>
            <input type="text" required value={formData.name} onChange={set('name')}
              placeholder="Enter your name" autoComplete="name" style={authInputStyle} onFocus={authInputFocus} onBlur={authInputBlur} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={authLabelStyle}>Email</label>
            <input type="email" required value={formData.email} onChange={set('email')}
              placeholder="Enter your email" autoComplete="email" style={authInputStyle} onFocus={authInputFocus} onBlur={authInputBlur} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={authLabelStyle}>Phone (10 digits)</label>
            <input type="tel" required value={formData.phone} onChange={e => { trackInteraction(); setFormData(p => ({ ...p, phone: e.target.value.replace(/[^0-9]/g, '').slice(0, 10) })); }}
              placeholder="9876543210" maxLength={10} autoComplete="tel" style={authInputStyle} onFocus={authInputFocus} onBlur={authInputBlur} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={authLabelStyle}>Password</label>
            <div style={{ position: 'relative' }}>
              <input type={showPassword ? 'text' : 'password'} required value={formData.password} onChange={set('password')}
                placeholder="Min 8 characters" autoComplete="new-password"
                style={{ ...authInputStyle, paddingRight: 44 }} onFocus={authInputFocus} onBlur={authInputBlur} />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#666', cursor: 'pointer', padding: 4 }}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={authLabelStyle}>Confirm Password</label>
            <div style={{ position: 'relative' }}>
              <input type={showConfirmPassword ? 'text' : 'password'} required value={formData.confirmPassword} onChange={set('confirmPassword')}
                placeholder="Confirm your password" autoComplete="new-password"
                style={{ ...authInputStyle, paddingRight: 44 }} onFocus={authInputFocus} onBlur={authInputBlur} />
              <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#666', cursor: 'pointer', padding: 4 }}>
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
            <input type="checkbox" id="terms" checked={formData.agreeToTerms} onChange={set('agreeToTerms')}
              style={{ width: 16, height: 16, accentColor: '#FFD93D', cursor: 'pointer' }} />
            <label htmlFor="terms" style={{ color: '#888', fontSize: 13, cursor: 'pointer' }}>
              I agree to the <a href="/terms" style={{ color: '#FFD93D', textDecoration: 'none' }}>Terms</a> and{' '}
              <a href="/privacy" style={{ color: '#FFD93D', textDecoration: 'none' }}>Privacy Policy</a>
            </label>
          </div>

          <button type="submit" disabled={isSubmitting} style={authButtonStyle(isSubmitting)}>
            {isSubmitting ? <><Loader2 size={18} className="animate-spin" /> Creating account...</> : 'Create Account'}
          </button>
        </form>

        <p style={{ color: '#666', fontSize: 14, marginTop: 24, textAlign: 'center' }}>
          Already have an account?{' '}
          <a href="/auth/login" style={{ color: '#FFD93D', fontWeight: 600, textDecoration: 'none' }}>Sign in</a>
        </p>
      </AuthShell>
    </>
  );
};

export default RegisterPage;
